import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileAudio, Upload, Loader2, Mic2, Captions } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";

const LANGS = [["es", "Español"], ["en", "English"], ["pt", "Português"], ["fr", "Français"], ["de", "Deutsch"], ["it", "Italiano"]];

export const AudioPanel = ({ usage, narration, setNarration, audioMode, setAudioMode, onScenes, onScript }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("es");
  const [status, setStatus] = useState("");

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/narration/upload", fd);
      setNarration(data); toast.success("Audio subido. Ahora transcríbelo.");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const transcribe = async () => {
    setBusy(true); setStatus("Transcribiendo audio (motor local Vosk)…");
    try {
      await api.post(`/narration/${narration.id}/transcribe`, { lang });
      for (let i = 0; i < 300; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/narration/${narration.id}`);
        if (data.transcript_status === "failed") throw new Error(data.transcript_error || "Falló la transcripción");
        if (data.transcript_status === "done") {
          if (!data.segments.length) throw new Error("No se detectó voz en el audio");
          if (audioMode === "use") { onScenes(data.scenes); toast.success(`${data.scenes.length} escenas sincronizadas con tu audio`); }
          else { onScript(data.text); toast.success("Texto transcrito: elige voz y estilo Piper y renderiza"); }
          setStatus(`Transcripción lista · ${data.segments.length} frases`);
          return;
        }
      }
    } catch (e) { toast.error(errMsg(e)); setStatus(""); } finally { setBusy(false); }
  };

  const Opt = ({ id, icon: I, title, desc }) => (
    <button data-testid={`audio-mode-${id}`} onClick={() => setAudioMode(id)}
      className={`text-left p-4 rounded-xl border transition-colors ${audioMode === id ? "border-[#E8C47C] bg-[#E8C47C]/10" : "border-white/10 hover:border-white/25"}`}>
      <I className="h-5 w-5 text-[#E8C47C]" /><p className="mt-2 text-sm text-white font-medium">{title}</p><p className="text-xs text-zinc-500 mt-1">{desc}</p>
    </button>
  );

  return (
    <div className="panel p-6 space-y-4" data-testid="audio-panel">
      <div className="grid sm:grid-cols-2 gap-3">
        <Opt id="use" icon={Mic2} title="A · Usar mi audio como narración" desc="Tu audio original suena en el video. Se transcribe para crear escenas y subtítulos sincronizados." />
        <Opt id="tts" icon={Captions} title="B · Solo transcribir y narrar con Piper" desc="Extraemos el texto de tu audio y generamos una narración nueva con la voz y estilo que elijas." />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <FileAudio className="h-6 w-6 text-[#E8C47C]" />
        <p className="text-sm text-white flex-1 min-w-[180px]" data-testid="narration-name">{narration ? `${narration.title} · ${narration.duration}s` : usage?.uploads ? "Sube un MP3, WAV, M4A…" : "Disponible en planes de pago"}</p>
        <input ref={fileRef} type="file" accept="audio/*" hidden onChange={upload} data-testid="narration-upload-input" />
        <Button data-testid="narration-upload-btn" variant="outline" disabled={!usage?.uploads || busy} onClick={() => fileRef.current.click()} className="border-white/10"><Upload className="h-4 w-4 mr-2" />Subir audio</Button>
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="transcribe-lang" className="h-9 rounded-md bg-zinc-900 border border-white/10 text-sm px-2">
          {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button data-testid="transcribe-btn" disabled={!narration || busy} onClick={transcribe} className="bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Transcribir"}
        </Button>
      </div>
      {status && <p className="text-xs font-mono-tc text-zinc-400" data-testid="transcribe-status">{status}</p>}
    </div>
  );
};
